import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as game from '@/lib-backend/methods/game';
import { GameKind, GameTemplateWithStatus } from '@/lib-backend';

export type CreateTemplateResult =
    | { kind: 'ok'; template: GameTemplateWithStatus }
    | { kind: 'error'; reason: game.CreateGameTemplateResult };

export type UpdateTemplateResult =
    | { kind: 'ok'; template: GameTemplateWithStatus }
    | { kind: 'error'; reason: game.UpdateGameTemplateResult };

@Injectable({ providedIn: 'root' })
export class GameTemplatesService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly templates = signal<GameTemplateWithStatus[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    readonly gameKinds = signal<GameKind[]>([]);
    readonly gameKindsLoading = signal(false);

    async loadTemplates(): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(game.listGameTemplates());
            this.templates.set(resp.Templates);
        } catch (e) {
            this.error.set('Failed to load game templates.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async ensureTemplatesLoaded(): Promise<void> {
        if (this.templates().length > 0 || this.loading()) return;
        await this.loadTemplates();
    }

    async loadGameKinds(): Promise<void> {
        this.gameKindsLoading.set(true);
        try {
            const resp = await this.api.client.call(game.listGameKinds());
            this.gameKinds.set(resp.GameKinds);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
        } finally {
            this.gameKindsLoading.set(false);
        }
    }

    async ensureGameKindsLoaded(): Promise<void> {
        if (this.gameKinds().length > 0 || this.gameKindsLoading()) return;
        await this.loadGameKinds();
    }

    async createTemplate(params: game.CreateGameTemplateRequest): Promise<CreateTemplateResult> {
        try {
            const resp = await this.api.client.call(game.createGameTemplate(params));
            if (resp.Result === 'Ok') {
                const created = this.resolveGameKind(resp.Template);
                this.templates.update((list) => [...list, created]);
                this.notification.show({ severity: 'success', summary: 'Game template created' });
                return { kind: 'ok', template: created };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    async updateTemplate(params: game.UpdateGameTemplateRequest): Promise<UpdateTemplateResult> {
        try {
            const resp = await this.api.client.call(game.updateGameTemplate(params));
            if (resp.Result === 'Ok') {
                let updated: GameTemplateWithStatus | null = null;
                this.templates.update((list) =>
                    list.map((t) => {
                        if (t.Template.Id !== resp.Template.Template.Id) return t;
                        // The update response does not re-resolve GameKind, so the
                        // Mode / Win mode / Game kind columns would blank out until
                        // a reload. Re-resolve it from the loaded kinds, falling
                        // back to the row's previous (immutable) GameKind.
                        updated = this.resolveGameKind(
                            resp.Template,
                            t.Template.GameKind ?? null,
                        );
                        return updated;
                    })
                );
                this.notification.show({ severity: 'success', summary: 'Game template updated' });
                return { kind: 'ok', template: updated ?? resp.Template };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    /**
     * Fill a template's resolved `GameKind` when the API response omits it (the
     * create/update endpoints return only `GameKindId`). Resolves from the loaded
     * `gameKinds`, else uses the supplied fallback (e.g. the row's prior value).
     */
    private resolveGameKind(
        t: GameTemplateWithStatus,
        fallback: GameKind | null = null,
    ): GameTemplateWithStatus {
        if (t.Template.GameKind) return t;
        const kind =
            this.gameKinds().find(
                (k) => k.GameKindId === t.Template.GameKindId,
            ) ?? fallback;
        return kind
            ? { ...t, Template: { ...t.Template, GameKind: kind } }
            : t;
    }
}
